/**
 * Inner Circle Subscription Builder
 * Handles product selection, quantity tracking, and cart submission for subscriptions
 */

class MembershipBuilder {
  constructor(section) {
    this.section = section;
    this.config = JSON.parse(section.querySelector('.membership-builder__config').textContent);
    this.products = section.querySelectorAll('.membership-builder__product');
    this.summaryItems = section.querySelector('.membership-builder__summary-items');
    this.summaryEmpty = section.querySelector('.membership-builder__summary-empty');
    this.totalAmount = section.querySelector('.membership-builder__total-amount');
    this.minimumNotice = section.querySelector('.membership-builder__minimum-notice');
    this.progressFill = section.querySelector('.membership-builder__progress-fill');
    this.remainingAmount = section.querySelector('.membership-builder__remaining-amount');
    this.submitButton = section.querySelector('.membership-builder__submit');
    this.frequencySelect = section.querySelector('.membership-builder__frequency-select');
    this.savingsContainer = section.querySelector('.membership-builder__savings');
    this.originalPriceEl = section.querySelector('.membership-builder__original-price');
    this.savingsAmountEl = section.querySelector('.membership-builder__savings-amount');

    this.selections = new Map(); // Map of variantId -> { quantity, price, title, productId, sellingPlanId }
    this.totalInCents = 0;

    this.init();
  }

  init() {
    this.bindEvents();
    this.updateSummary();
  }

  bindEvents() {
    // Quantity buttons
    this.products.forEach(product => {
      const decreaseBtn = product.querySelector('[data-action="decrease"]');
      const increaseBtn = product.querySelector('[data-action="increase"]');
      const qtyInput = product.querySelector('.membership-builder__qty-input');
      const variantSelect = product.querySelector('.membership-builder__variant-dropdown');

      decreaseBtn?.addEventListener('click', () => this.updateQuantity(product, -1));
      increaseBtn?.addEventListener('click', () => this.updateQuantity(product, 1));

      qtyInput?.addEventListener('change', (e) => {
        const newQty = Math.max(0, Math.min(99, parseInt(e.target.value) || 0));
        e.target.value = newQty;
        this.setQuantity(product, newQty);
      });

      // Variant selector (when user changes pack size dropdown)
      variantSelect?.addEventListener('change', (e) => {
        const selectedOption = e.target.options[e.target.selectedIndex];
        const newVariantId = e.target.value;

        // Update product data attributes with new variant info
        product.dataset.variantId = newVariantId;
        product.dataset.price = selectedOption.dataset.price;

        // Re-add with the new variant info if there's a quantity
        // setQuantity uses productId_flavor as key, so changing variant
        // within same flavor just updates the existing selection
        const currentQty = parseInt(qtyInput.value) || 0;
        if (currentQty > 0) {
          this.setQuantity(product, currentQty);
        }
      });
    });

    // Submit button
    this.submitButton?.addEventListener('click', () => this.submitOrder());
  }

  updateQuantity(product, delta) {
    const qtyInput = product.querySelector('.membership-builder__qty-input');
    const currentQty = parseInt(qtyInput.value) || 0;
    const newQty = Math.max(0, Math.min(99, currentQty + delta));
    qtyInput.value = newQty;
    this.setQuantity(product, newQty);
  }

  setQuantity(product, quantity) {
    const variantId = product.dataset.variantId;
    const price = parseInt(product.dataset.price) || 0;
    const productId = product.dataset.productId;
    const flavor = product.dataset.flavor || '';

    // Create selection key: productId_flavor for flavor rows, or just variantId for simple view
    // This allows multiple flavors of the same product to be selected
    const selectionKey = flavor
      ? `${productId}_${flavor.toLowerCase().replace(/\s+/g, '-')}`
      : variantId;

    // Build title from flavor + selected option (if dropdown exists)
    let title = product.dataset.title || product.dataset.flavor || 'Item';
    const dropdown = product.querySelector('.membership-builder__variant-dropdown');
    if (dropdown) {
      const selectedText = dropdown.options[dropdown.selectedIndex]?.text || '';
      // Extract just the pack/size info before the price
      const packInfo = selectedText.split('—')[0]?.trim();
      if (packInfo && flavor) {
        title = `${flavor} (${packInfo})`;
      } else if (packInfo) {
        title = `${product.dataset.title} (${packInfo})`;
      }
    }

    // Get selling plan for this product
    const sellingPlanData = JSON.parse(
      product.querySelector('.membership-builder__selling-plans')?.textContent || '{"plans":[]}'
    );
    const sellingPlanId = sellingPlanData.plans[0]?.id || null;

    if (quantity > 0) {
      this.selections.set(selectionKey, {
        variantId,  // Store the actual variant ID for cart submission
        quantity,
        price,
        title,
        productId,
        sellingPlanId
      });
      product.dataset.selected = 'true';
    } else {
      this.selections.delete(selectionKey);
      product.dataset.selected = 'false';
    }

    this.updateSummary();
  }

  updateSummary() {
    // Calculate total
    this.totalInCents = 0;
    let itemsHtml = '';

    this.selections.forEach((item, selectionKey) => {
      const lineTotal = item.price * item.quantity;
      this.totalInCents += lineTotal;
      itemsHtml += `
        <div class="membership-builder__summary-item" data-selection-key="${selectionKey}">
          <span>${item.quantity}× ${item.title}</span>
          <span>${this.formatMoney(lineTotal)}</span>
        </div>
      `;
    });

    // Update items list
    if (this.summaryItems) {
      if (this.selections.size > 0) {
        this.summaryItems.innerHTML = itemsHtml;
        this.summaryEmpty?.classList.add('hidden');
      } else {
        this.summaryItems.innerHTML = '';
        if (this.summaryEmpty) {
          this.summaryEmpty.classList.remove('hidden');
          this.summaryItems.appendChild(this.summaryEmpty);
        }
      }
    }

    // Update total display
    if (this.totalAmount) {
      this.totalAmount.textContent = this.formatMoney(this.totalInCents);
    }

    // Calculate and display savings (20% discount means original = discounted / 0.8)
    if (this.savingsContainer && this.originalPriceEl && this.savingsAmountEl) {
      if (this.totalInCents > 0) {
        const originalTotal = Math.round(this.totalInCents / 0.8);
        const savings = originalTotal - this.totalInCents;
        this.originalPriceEl.textContent = this.formatMoney(originalTotal);
        this.savingsAmountEl.textContent = this.formatMoney(savings);
        this.savingsContainer.style.display = 'block';
      } else {
        this.savingsContainer.style.display = 'none';
      }
    }

    // Update minimum progress
    const minimumInCents = this.config.minimumOrder || 5000; // Default £50
    const minimumMet = this.totalInCents >= minimumInCents;
    const progress = Math.min(100, (this.totalInCents / minimumInCents) * 100);

    if (this.progressFill) {
      this.progressFill.style.width = `${progress}%`;
    }

    if (this.minimumNotice) {
      this.minimumNotice.dataset.met = minimumMet.toString();
    }

    if (!minimumMet && this.remainingAmount) {
      const remaining = minimumInCents - this.totalInCents;
      this.remainingAmount.textContent = this.formatMoney(remaining);
    }

    // Enable/disable submit
    if (this.submitButton) {
      this.submitButton.disabled = !minimumMet || this.selections.size === 0;
    }
  }

  formatMoney(cents) {
    // Simple GBP formatting
    return '£' + (cents / 100).toFixed(2);
  }

  async submitOrder() {
    if (this.selections.size === 0 || this.totalInCents < this.config.minimumOrder) {
      return;
    }

    this.submitButton.classList.add('loading');
    this.submitButton.disabled = true;

    try {
      // Build items array for cart add
      const items = [];

      this.selections.forEach((item) => {
        const cartItem = {
          id: parseInt(item.variantId),  // Use variantId from selection object
          quantity: item.quantity,
          properties: {
            _subscription_source: 'pemmican-club'
          }
        };

        // Add selling plan if available
        if (item.sellingPlanId) {
          cartItem.selling_plan = item.sellingPlanId;
        }

        items.push(cartItem);
      });

      // Add all items to cart
      const response = await fetch(this.config.cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      const result = await response.json();

      if (result.status) {
        // Error occurred
        console.error('Cart add error:', result);
        alert(result.description || 'Failed to add items to cart. Please try again.');
        return;
      }

      // Tag the cart with subscription source for order-level tracking
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { subscription_source: 'pemmican-club' }
        })
      }).catch(() => {});

      // Success - publish cart update event if available
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'membership-builder',
          cartData: result
        });
      }

      // Open cart drawer or redirect to cart
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');

      if (cartDrawer) {
        // Fetch updated cart sections and render
        const sectionsResponse = await fetch(`${this.config.cartUrl}?sections=cart-drawer,cart-icon-bubble`);
        const sections = await sectionsResponse.json();
        cartDrawer.renderContents({ sections });
      } else if (cartNotification) {
        const sectionsResponse = await fetch(`${this.config.cartUrl}?sections=cart-notification-product,cart-notification-button,cart-icon-bubble`);
        const sections = await sectionsResponse.json();
        cartNotification.renderContents({ sections });
      } else {
        // Redirect to cart page
        window.location.href = this.config.cartUrl;
      }

      // Reset selections
      this.resetBuilder();

    } catch (error) {
      console.error('Failed to add to cart:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      this.submitButton.classList.remove('loading');
      this.submitButton.disabled = false;
    }
  }

  resetBuilder() {
    this.selections.clear();
    this.products.forEach(product => {
      const qtyInput = product.querySelector('.membership-builder__qty-input');
      if (qtyInput) qtyInput.value = 0;
      product.dataset.selected = 'false';
    });
    this.updateSummary();
  }
}

// Initialize all builders on page
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.membership-builder').forEach(section => {
    new MembershipBuilder(section);
  });
});

// Also initialize if added dynamically (e.g., Shopify theme editor)
if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.membership-builder');
    if (section) {
      new MembershipBuilder(section);
    }
  });
}
